import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Star,
  Shield,
  MoreVertical,
  TrendingUp,
  Users,
  Eye,
  Calendar,
  FileText,
  Activity
} from 'lucide-react';

// Mock community data
const mockCommunityDetail = {
  id: '1',
  name: 'Chess Masters Club',
  sport: 'Chess',
  status: 'active',
  members: 1247,
  activityTrend: 'up',
  featured: false,
  bannerImage: null,
  description: 'A vibrant community for chess enthusiasts of all skill levels. We organize regular tournaments, training sessions, and strategy discussions.',
  rules: [
    'Be respectful to all members',
    'No inappropriate language or behavior',
    'Share knowledge and help beginners',
    'Follow tournament rules and guidelines'
  ],
  createdDate: '2024-01-15',
  createdBy: 'John Smith',
  membersList: [
    {
      id: '1',
      name: 'Sarah Johnson',
      avatar: null,
      joinedDate: '2024-01-15',
      role: 'admin'
    },
    {
      id: '2',
      name: 'Mike Chen',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
      joinedDate: '2024-02-01',
      role: 'coach'
    },
    {
      id: '3',
      name: 'Emma Davis',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face',
      joinedDate: '2024-02-15',
      role: 'player'
    }
  ],
  posts: [],
  events: [],
  moderationLogs: []
};

const CommunityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'about' | 'people' | 'posts' | 'events' | 'moderation'>('about');
  const [community, setCommunity] = useState(mockCommunityDetail);
  const [memberSearch, setMemberSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-gray-100 text-gray-800';
      case 'coach': return 'bg-yellow-100 text-yellow-800';
      case 'player': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleFeatureCommunity = () => {
    setCommunity(prev => ({ ...prev, featured: !prev.featured }));
    // API call would go here
  };

  const handleSuspendCommunity = () => {
    if (window.confirm(`${community.status === 'suspended' ? 'Reactivate' : 'Suspend'} ${community.name}?`)) {
      setCommunity(prev => ({
        ...prev,
        status: prev.status === 'suspended' ? 'active' : 'suspended'
      }));
      // API call would go here
    }
  };

  const filteredMembers = community.membersList.filter(member =>
    member.name.toLowerCase().includes(memberSearch.toLowerCase())
  );

  const UserAvatar: React.FC<{ user: any; size?: 'sm' | 'md' }> = ({ user, size = 'md' }) => {
    const sizeClasses = {
      sm: 'w-8 h-8 text-xs',
      md: 'w-10 h-10 text-sm'
    };

    return (
      <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center ${
        user.avatar ? '' : 'bg-gray-200 text-gray-600 font-medium'
      }`}>
        {user.avatar ? (
          <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
        ) : (
          getInitials(user.name)
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Back Navigation */}
      <button
        onClick={() => navigate('/admin/communities')}
        className="inline-flex items-center text-blue-600 hover:text-blue-800"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Communities
      </button>

      {/* Banner Image */}
      <div className="w-full h-40 bg-gray-200 rounded-lg flex items-center justify-center">
        {community.bannerImage ? (
          <img src={community.bannerImage} alt="Community banner" className="w-full h-full rounded-lg object-cover" />
        ) : (
          <div className="text-gray-400">Community Banner</div>
        )}
      </div>

      {/* Community Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          {/* Community Info */}
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">{community.name}</h1>
              <span className="px-2 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
                #{community.sport}
              </span>
              <span className={`px-2 py-1 text-sm font-medium rounded-full ${getStatusBadgeColor(community.status)}`}>
                {community.status.charAt(0).toUpperCase() + community.status.slice(1)}
              </span>
            </div>
            <div className="flex items-center space-x-1 text-gray-600">
              <Users className="w-4 h-4" />
              <span>{community.members.toLocaleString()} members</span>
              {community.activityTrend === 'up' && (
                <TrendingUp className="w-4 h-4 text-green-600 ml-2" />
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <button
              onClick={handleFeatureCommunity}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium ${
                community.featured
                  ? 'text-yellow-800 bg-yellow-100 hover:bg-yellow-200'
                  : 'text-gray-900 bg-yellow-400 hover:bg-yellow-500'
              }`}
            >
              <Star className="w-4 h-4 mr-2" />
              {community.featured ? 'Featured' : 'Feature'}
            </button>
            <button
              onClick={handleSuspendCommunity}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                community.status === 'suspended'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              <Shield className="w-4 h-4 mr-2" />
              {community.status === 'suspended' ? 'Reactivate' : 'Suspend'}
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-600">
              <MoreVertical className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'about', label: 'About', icon: FileText },
            { key: 'people', label: 'People', icon: Users },
            { key: 'posts', label: 'Posts', icon: FileText },
            { key: 'events', label: 'Events', icon: Calendar },
            { key: 'moderation', label: 'Moderation Logs', icon: Activity }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {activeTab === 'about' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Community Information</h3>
              <p className="text-gray-600 leading-relaxed">{community.description}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Community Rules</h3>
              <ul className="space-y-2">
                {community.rules.map((rule, index) => (
                  <li key={index} className="flex items-start space-x-2 text-gray-600">
                    <span className="text-gray-400">•</span>
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Created Date</label>
                <p className="text-sm text-gray-900">{new Date(community.createdDate).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Created By</label>
                <p className="text-sm text-gray-900">{community.createdBy}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'people' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Members ({filteredMembers.length})
              </h3>
              <div className="w-64">
                <input
                  type="text"
                  placeholder="Search members..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="space-y-3">
              {filteredMembers.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <UserAvatar user={member} />
                    <div>
                      <p className="font-medium text-gray-900">{member.name}</p>
                      <p className="text-sm text-gray-500">
                        Joined {new Date(member.joinedDate).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(member.role)}`}>
                      {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                    </span>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {filteredMembers.length === 0 && (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No members found</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'posts' && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No posts yet</h3>
            <p className="text-gray-500">Community posts will appear here when members start sharing.</p>
          </div>
        )}

        {activeTab === 'events' && (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No events scheduled</h3>
            <p className="text-gray-500">Community events will appear here when they are created.</p>
          </div>
        )}

        {activeTab === 'moderation' && (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No moderation logs</h3>
            <p className="text-gray-500">Moderation activities will be logged here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityDetail;